import { Box, Typography, Grid, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo } from 'react';
import strings from '../../Globalization/index';
import NextPrevComponent from '../../components/common/NextPrevComponent';
import { ProcessMonitoringStatus } from '../ProcessMonitoringStatus';
import connectionService from '../../services/Connection';
import consts from '../../consts';
import { useAdvantageCycleInfo } from './Context';

const LabelValueEntry = ({ 
    label, 
    element, 
    value, 
    size, 
    lgOffset, 
    mdOffset, 
    xsOffset, 
    HeaderLabel, 
    index 
}) => {
    return (
        <Grid
            size={{ xs: 12, md: 6, lg: parseInt(size) }}
            lgoffset={lgOffset}
            mdoffset={mdOffset}
            xsoffset={xsOffset}
            className="Label-Value-Entry"
            key={`col-${index}`}
        >
            <Typography
                className="LabelBold"
                fontSize="inherit"
                component="span"
            >
                {label || strings[HeaderLabel] || HeaderLabel}
            </Typography>
            <Typography
                className="Boundary"
                fontSize="inherit"
                component="span"
            >
                :
            </Typography>
            <Typography fontSize="inherit" component="span">
                {HeaderLabel === consts.processMonitoring ?
                    <ProcessMonitoringStatus CriticalParameterIcon={value} align='left' ml={0} />
                    : element || value
                }
            </Typography>
        </Grid>
    );
};

const TableValueEntry = ({ 
    label, 
    value = '', 
    size, 
    lgOffset, 
    mdOffset, 
    xsOffset, 
    HeaderLabel, 
    index 
}) => {
	const valuesArr = value.split(',');
	return (
		<Grid
			size={{ xs: 12, md: 6, lg: parseInt(size) }}
			lgoffset={lgOffset}
			mdoffset={mdOffset}
			xsoffset={xsOffset}
			key={`col-${index}`}
			sx={{
				'& .LabelBold': {
					fontWeight: 'bold'
				}
			}}
		>
			<Stack>
				<Typography
					className="LabelBold"
					fontSize="inherit"
					component="span"
				>
					{label || strings[HeaderLabel] || HeaderLabel}
				</Typography>
				{valuesArr.map((val, i) => (
					<Typography
						fontSize="inherit"
						component="span"
						key={`val-${i}`}
					>
						{val}
					</Typography>
				))}
			</Stack>
		</Grid>
	);
}

const TopSection = () => {
	const theme = useTheme();
	const { data, topSection, onNext, onPrev } = useAdvantageCycleInfo();
	return ( 
		<NextPrevComponent onNext={onNext} onPrev={onPrev}>
			<Grid container>
				<Box sx={{ flexGrow: 1 }}>
					<Grid container>
						<Grid size={{xs: 12, md: 3, lg: 2}}>
							<Box sx={{ flexGrow: 1, textAlign: 'center' }}>
								<Grid container spacing={1}>
									<Grid size={12}>
										<Box
											component="img"
											src={`${connectionService.PTXImage}/${data.CycleImageUrl}?v=${new Date()}`}
											alt="washer"
											sx={{ height: 'auto', width: theme.spacing(7) }}
										/>
									</Grid>

									<Grid size={12}>
										{!!data?.EquipmentTypeName && (
											<Typography
												className="washer-serialNumber"
												variant="subtitle1"
												fontWeight={'bold'}
												fontSize="20px"
												title={strings.EquipmentTypeName}
												component="span"
											>
												{data.EquipmentTypeName}
											</Typography>
										)}
									</Grid>
									<Grid size={12}>
										{!!data?.EquipmentName && (
											<Typography
												className="washer-serialNumber"
												variant="subtitle2"
												fontWeight={'bold'}
												title={strings.EquipmentName}
												component="span"
											>
												{data.EquipmentName}
											</Typography>
										)}
									</Grid>
									<Grid size={12}>
										{!!data?.SerialNumber && (
											<Typography
												className="washer-serialNumber"
												variant="subtitle2"
												fontWeight={'bold'}
												title={strings.SerialNumber}
												component="span"
												fontSize="14px"
											>
												{data.SerialNumber === '-1' ? '' : data.SerialNumber}
											</Typography>
										)}
									</Grid>
								</Grid>
							</Box>
						</Grid>
						<Grid
							size={{xs: 12, md: 9, lg: 10}}
							rowSpacing={1}
							container
							sx={{
								height: 'fit-content',
								'& .Label-Value-Entry': {
									display: 'grid',
									gridTemplateColumns: '1fr 1em 1fr',
									gridTemplateRows: '1fr',
									gap: '8px 5px',
									fontSize: 13,
									'& .LabelBold': {
										fontWeight: 'bold'
									},
									'& .Boundary': {
										fontWeight: 'bold'
									}
								}
							}}
						>
							{topSection.map(
								(row, i) =>
									row?.length > 0 && (
										<Grid item container rowSpacing={1} size={12} key={`row-${i}`}>
											{row.reduce(
												(acc, { label, element, Value: value, size, LgOffset: lgOffset, mdOffset, xsOffset, HeaderLabel, DataProperty, tableView }, j) => {
													const commonProps = {
														value,
														element,
														size,
														lgOffset,
														mdOffset,
														xsOffset,
														HeaderLabel,
														index: j,
														key: `col-${j}`
													};
												
													return acc.concat([
														tableView ? (<TableValueEntry
															{...commonProps}
														/>) : (<LabelValueEntry
															label={label}
															{...commonProps}
														/>)
													]);
												},
												[]
											)}
										</Grid>
									)
							)}
							<Box sx={{ flexGrow: 1 }}>
								{data.CycleName === 'Chemistry Change' ? (
									<Grid container spacing={1}>
										<Grid size={12}>
											<Box
												sx={{
													display: 'grid',
													gridTemplateColumns: '1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr',
													gridTemplateRows: '1fr 1fr',
													gap: '3px 5px',
													gridTemplateAreas: '". . . . . . . . ."\n    ". . . . . . . . ."',
													'& .LabelBold': {
														fontWeight: 'bold'
													}
												}}
											>
											</Box>
										</Grid>
									</Grid>
								) : (
									<Grid container rowGap={1}>
										<Grid size={12}>
											<Box
												sx={{
													display: 'grid',
													gridTemplateColumns: '1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr',
													gridTemplateRows: '1fr 1fr',
													gap: '3px 5px',
													gridTemplateAreas: '". . . . . . . . ."\n    ". . . . . . . . ."',
													'& .LabelBold': {
														fontWeight: 'bold'
													}
												}}
											>
											</Box>
										</Grid>
									</Grid>
								)}
							</Box>
						</Grid>
					</Grid>
				</Box>
			</Grid>
		</NextPrevComponent>
	);
};

export default memo(TopSection);




